<template>
  <UIDialog
    :is-open
    :title="`Account Ownership for ${assetName}`"
    :z-index
    size="2md"
    flat
    @close="handleClose"
  >
    <div class="precisions">
      <div class="precisions__actions">
        <div class="precisions__actions-buttons">
          <UIButton
            :leading="ArrowDownTrayIcon"
            label="Import"
            variant="gray"
            fill="light"
            size="sm"
            :disabled="isProcessing"
            @click="handleImport"
          />
          <UIButton
            :leading="ArrowUpTrayIcon"
            label="Export"
            variant="gray"
            fill="light"
            size="sm"
            :disabled="isProcessing"
            @click="handleExport"
          />
        </div>
        <div class="precisions__actions-buttons">
          <div class="precisions__date">
            <span>Date:</span>
            <UIInputDate
              v-model="date"
              size="sm"
              :disabled="isProcessing"
            />
          </div>
          <UIButton
            v-if="listValues.length > 0"
            label="Owner"
            variant="secondary"
            size="sm"
            :leading="PlusIcon"
            :disabled="isProcessing"
            @click="handleAddOwner"
          />
        </div>
      </div>
      <UIGrid
        ref="gridRef"
        v-model:sort="sort"
        :columns
        :items
        :footer
        editable
        :readonly="isProcessing"
        :value-paster
        unitary-sort
        :value-copier
        :item-snapshot
        :item-restore
      >
        <template #cellAccountId="{ columnName, item }">
          <UIDropdownSearchList
            v-if="!item[columnName]"
            :data="listValues"
            placement="bottom-start"
            @click:item="item[columnName] = $event"
          >
            <template #default="{ show }">
              <UIButton
                label="Select owner"
                size="sm"
                variant="gray"
                fill="outlined"
                class="w-full"
                :trailing="ChevronDownIcon"
                @click="show"
              />
            </template>
          </UIDropdownSearchList>
        </template>
        <template #cellAmount="{ columnName, item, editCell }">
          <UIInputPercent
            v-if="editCell"
            :model-value="item[columnName]"
            size="sm"
            @click:enter="handleEditClickEnter"
            @click:tab="handleEditClickTab"
            @click:escape="handleEditClickEscape"
            @update:model-value="item[columnName] = $event"
            @ready="handleReady"
          />
        </template>
        <template #actions="{ rowIndex }">
          <UIButton
            v-tooltip.top="'Delete owner'"
            variant="danger"
            fill="light"
            size="sm"
            :leading="TrashIcon"
            @click="handleDeleteOwner(rowIndex)"
          />
        </template>
        <template #footer="{ columnName, displayValue }">
          <template v-if="columnName === 'accountId'">
            {{ displayValue }}
          </template>
          <span
            v-else
            :class="totalClasses"
          >
            {{ displayValue }}
          </span>
        </template>
      </UIGrid>
      <UIButtonsGroup>
        <template #default="{ short }">
          <UIButtonClose
            :short
            @click="handleClose"
          />
          <UIButtonSave
            :short
            label="Update"
            type="submit"
            @click="handleComplete"
          />
        </template>
      </UIButtonsGroup>
    </div>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed, markRaw, ref, useTemplateRef, watch } from 'vue'
import { uniqBy } from 'lodash'

import { DropdownListData, ModalClass, Sort, TransactionSettings } from '@types'
import { OrganizeFamilyStructureAmountItem, OrganizeFamilyStructurePath } from '../utils/types'
import { TransactionAmountRule } from '@/entities/transactions/utils/enums'

import { ASSET_FIELD, UNKNOWN_VALUE } from '@/const'

import { displayAmount } from '@/entities/transactions/utils/helpers'
import { exportCSV } from '@/helpers/common'
import { convertDateToISO, getCurrentDate } from '@/helpers/dates'
import { numberClean, numberParse } from '@/helpers/numbers'
import useGridSort from '@/components/hooks/gridSort'

import { useNotifications } from '@/plugins/notification'

import useAnalyticsStore from '@/store/analytics'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useModalsStore } from '@/store/modals'

import { UIDialog, UIImportDialog } from '@ui/modals'
import { UIButton, UIButtonClose, UIButtonSave, UIButtonsGroup } from '@ui/buttons'
import { ArrowDownTrayIcon, ArrowUpTrayIcon, ChevronDownIcon, PlusIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { UIGrid } from '@ui/grid'
import { UIInputDate, UIInputPercent } from '@ui/fields'
import { UIDropdownSearchList } from '@ui/dropdowns'
import { generateId } from '@/entities/utils/helpers'

type Props = {
  modal: ModalClass<any>
  entrySettings: TransactionSettings['entries'][0]
  assetId: string
  paths: OrganizeFamilyStructurePath[]
  data: DropdownListData[]
  dates: Map<string, string>
}

type Emits = {
  'complete': [data: OrganizeFamilyStructureAmountItem[], date: string]
  'cancel': []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { error } = useNotifications()

const analyticsStore = useAnalyticsStore()
const assetsBunchStore = useAssetsBunchStore()
const modalsStore = useModalsStore()

const gridRef = useTemplateRef('gridRef')

const inputItems = ref<OrganizeFamilyStructureAmountItem[]>([])
const originalAccountIds = ref<string[]>([])
// buffered while the file is parsed, applied as a whole in `handleImportComplete`
let importedItems: OrganizeFamilyStructureAmountItem[] = []

const sort = ref<Sort[]>([])

const isProcessing = ref(false)

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)

const assetName = computed(() => 
  assetsBunchStore.getElementById(props.assetId)?.field<string>(ASSET_FIELD.NAME).value || UNKNOWN_VALUE
)

const accountTitle = computed(() => props.entrySettings.account?.title || 'Owner')

const defaultDate = computed(() => analyticsStore.getDate || getCurrentDate({}))

// One date for the whole form: every transaction the tree edit produces for this asset is
// dated with it. Prefilled with the date a previous run of this form already stored.
const date = ref<string>(defaultDate.value)

const isPercentAmount = computed(
  () => props.entrySettings.amount?.rule === TransactionAmountRule.PERCENT,
)

const columns = computed(() => [
  {
    name: 'accountId',
    caption: accountTitle.value,
    formatter: (value: string) => accountFormatter(value),
    uneditable: true,
  },
  {
    name: 'amount',
    caption: 'Percentage',
    formatter: (value: number) => amountFormatter(value),
  }
])

const items = useGridSort<OrganizeFamilyStructureAmountItem>(sort, columns, inputItems)

const totalAmount =  computed(() => {
  return items.value.reduce((acc, value) => {
    return acc + (value.amount || 0)
  }, 0)
})

const footer = computed(() => ({
  accountId: 'Total',
  amount: amountFormatter(totalAmount.value),
}))

const listValues = computed(() => {
  const filtered = props.data
    .filter(item => item.value !== props.assetId && !items.value.some(i => i.accountId === item.value))
  return uniqBy(filtered, 'value')
})

const totalClasses = computed(() => ({
  'precisions__total--positive':  totalAmount.value === 1,
  'precisions__total--negative':  totalAmount.value !== 1,
}))

const accountFormatter = (accountId: string) => {
  return assetsBunchStore.getElementById(accountId)?.field<string>(ASSET_FIELD.NAME).value || UNKNOWN_VALUE
}

const amountFormatter = (value: number | null | undefined) => {
  if (!props.entrySettings.amount) return ''
  return displayAmount(value, isPercentAmount.value)
}

const parseAmount = (value: number | string, isDisplayUnits = false) => {
  const raw = `${value}`.trim()
  if (!raw) return null
  const parsed = numberParse(raw)
  if (!Number.isFinite(parsed)) return null
  const scale = isPercentAmount.value && (isDisplayUnits || raw.includes('%'))
  return scale ? parsed / 100 : parsed
}

const handleReady = (e: HTMLInputElement) => {
  e.focus()
}

const handleEditClickEnter = async (event: Event) => {
  event.stopPropagation()
  gridRef.value?.release()
  gridRef.value?.clickEnter()
}

const handleEditClickTab = async (event: Event) => {
  event.stopPropagation()
  event.preventDefault()
  gridRef.value?.release()
  gridRef.value?.clickTab()
}

const handleEditClickEscape = (event: Event) => {
  event.stopPropagation()
  gridRef.value?.revert()
}

const handleClose = () => {
  emit('cancel')
  props.modal.close()
}

const handleComplete = () => {
  // UIInputDate reports the raw text when a typed date is incomplete or invalid
  // (`convertDateToISO(value) || value`), so it is converted back before it is applied.
  const isoDate = date.value ? convertDateToISO(date.value) : undefined
  if (!isoDate) {
    error({
      message: 'Invalid date',
      description: `"${date.value}" is not a valid date, fix it before updating the ownership.`,
    })
    return
  }
  // an owner the table no longer lists (dropped by an import) is reported with an empty
  // amount — that is what makes the update remove it from the tree
  const removed = originalAccountIds.value
    .filter(accountId => !items.value.some(item => item.accountId === accountId))
    .map(accountId => ({ id: generateId(), accountId, amount: null }))
  emit('complete', [...items.value, ...removed], isoDate)
  props.modal.close()
}

const handleAddOwner = () => {
  inputItems.value.push({ id: generateId(), accountId: undefined, amount: null })
  gridRef.value?.edit(items.value.length, 1)
}

const handleDeleteOwner = (index: number) => {
  const { id } = items.value[index] ?? {}
  if (!id) return
  inputItems.value = inputItems.value.filter(item => item.id !== id)
}

const handleImport = () => {
  const modalInstance = modalsStore.init('contacts-import', markRaw(UIImportDialog))
  modalInstance?.open(modalsStore.getZIndex(), {
    columns,
    title: 'Import account ownership',
    headersExample: 'account,amount',
    onImportStart: handleImportStart,
    onImportStep: handleImportStep,
    onImportComplete: handleImportComplete,
  })
}

const handleImportStart = () => {
  isProcessing.value = true
  importedItems = []
}

const handleImportStep = ({ data }: { data: any; index: number }) => {
  const accountId = assetsBunchStore.getElementByName(data.account)?.id
  const amount = parseAmount(data.amount)
  importedItems.push({ id: generateId(), accountId, amount })
}

// The file becomes the table: an ownership that is already there keeps its row and only
// takes the imported amount, an ownership the file does not list is dropped, and the rest
// of the file is added. Dropped ownerships are still sent to the update as empty amounts
// (see `handleComplete`) so the tree loses them too. A file that produced no rows at all
// (wrong format, aborted parse) changes nothing.
const handleImportComplete = () => {
  isProcessing.value = false
  if (!importedItems.length) return
  const identified = uniqBy(importedItems.filter(item => item.accountId), 'accountId')
  const unidentified = importedItems.filter(item => !item.accountId)
  importedItems = []
  const imported = identified.map(item => {
    const existing = inputItems.value.find(({ accountId }) => accountId === item.accountId)
    return existing ? { ...existing, amount: item.amount } : item
  })
  inputItems.value = [...imported, ...unidentified]
}

const handleExport = () => {
  const cols = ['account', 'amount']
  const headers = Object.fromEntries(cols.map(col => [col, col]))
  const data = items.value.map(item => {
    const account = item.accountId ? accountFormatter(item.accountId) : ''
    return { account, amount: item.amount }
  })
  exportCSV(`${assetName.value}-precision`, cols, [headers, ...data], false)
}

const valuePaster = (
  field: string,
  item: any,
  value: number | string,
  check?: boolean,
) => {
  switch (field) {
    case 'amount':
      item.amount = parseAmount(value, check === false)
      break
    case 'accountId':
      const accountId = assetsBunchStore.getElementByName(value as string)?.id
      item.accountId = accountId
      break
    default:
      break
  }
}

const valueCopier = (field: string, item: any) => field === 'amount' && item.amount != null ? `${numberClean(item.amount)}` : ''

// Rows are flat objects, so a shallow copy is the snapshot. Restoring in place keeps the
// reference the sorted `items` list holds.
const itemSnapshot = (item: OrganizeFamilyStructureAmountItem) => ({ ...item })

const itemRestore = (
  item: OrganizeFamilyStructureAmountItem,
  snapshot: OrganizeFamilyStructureAmountItem,
) => {
  Object.assign(item, snapshot)
}

watch(() => props.paths, value => {
  inputItems.value = uniqBy(
    value
      .filter(el => el.path.at(-1) === props.assetId)
      .map(el => ({ id: generateId(), accountId: el.path.at(-2), amount: el.amount })), 
    'accountId'
  )
  originalAccountIds.value = inputItems.value
    .map(item => item.accountId)
    .filter((accountId): accountId is string => !!accountId)
  const storedDate = inputItems.value
    .map(item => item.accountId && props.dates.get(`${item.accountId}-${props.assetId}`))
    .find(Boolean)
  date.value = storedDate || defaultDate.value
}, { immediate: true })
</script>

<style scoped>
.precisions {

  &__actions {
    @apply flex justify-between;
    @apply p-3;
  }

  &__actions-buttons {
    @apply flex items-center gap-2;
  }

  &__date {
    @apply flex items-center gap-2;
    @apply text-sm text-gray-400;

    .v-popper {
      @apply w-32;
    }
  }

  &__total {

    &--positive {
      @apply text-gray-950 dark:text-green-400;
    }
    &--negative {
      @apply text-red-600 dark:text-red-400;
    }

    & + & {
      @apply col-span-2;
    }
  }

  .ui-buttons-group {
    @apply py-4 px-default;
  }
}
</style>
